import { getApp, getApps, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  initializeAuth,
  onIdTokenChanged,
  type Persistence,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
  updatePassword,
} from "firebase/auth";

export type { User };
export {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
};

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// ── Remember-me persistence ────────────────────────────────────────────────
//
// • Remember me ON  → `browserLocalPersistence`: refresh token persists in
//   localStorage across browser closes (Firebase keeps it valid for as long
//   as the user isn't disabled, which covers the "come back in 2 days" case).
// • Remember me OFF → `browserSessionPersistence`: refresh token lives only
//   for the current tab session; closing the browser signs the user out.
//
// We store the preference in localStorage so it survives reloads. Default is
// ON (most common UX expectation when the toggle is missing/older sessions).

const REMEMBER_ME_KEY = "auth:remember-me";

function readRememberMe(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const v = window.localStorage.getItem(REMEMBER_ME_KEY);
    return v === null ? true : v === "true";
  } catch {
    // silent: blocked site data throws here at import; Firebase then skips
    // the unavailable tiers and keeps the session in memory.
    return true;
  }
}

function persistenceFor(remember: boolean): Persistence {
  return remember ? browserLocalPersistence : browserSessionPersistence;
}

/**
 * Where Firebase looks for the signed-in user at boot, remembered tier first.
 *
 * Booting must not touch storage. `getAuth()` lists IndexedDB first, so with
 * a `setPersistence(local)` after it every page load moved the user out of
 * localStorage, waited on Identity Platform, and only then wrote it back.
 * Other open tabs read localStorage inside that gap, dropped to signed-out in
 * memory, and sent requests with no Bearer token (`AUTH_MISSING`) — upstream
 * firebase-js-sdk #8282 / #9319.
 *
 * IndexedDB is left out entirely: Firebase opens every listed tier at boot,
 * and IndexedDB hangs in Safari with several tabs open (#8019 / #9802).
 */
function persistenceHierarchy(remember: boolean): Persistence[] {
  return remember
    ? [browserLocalPersistence, browserSessionPersistence]
    : [browserSessionPersistence, browserLocalPersistence];
}

const auth = initializeAuth(app, {
  persistence: persistenceHierarchy(readRememberMe()),
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Only set tenantId when the env var is a non-empty string; otherwise Firebase
// silently stores `undefined` and later throws an opaque error on sign-in.
const tenantId = import.meta.env.VITE_FIREBASE_TENANT_ID;
if (typeof tenantId === "string" && tenantId.length > 0) {
  auth.tenantId = tenantId;
}

export function getRememberMe(): boolean {
  return readRememberMe();
}

/**
 * Apply (and persist) the user's "remember me" preference. Call this BEFORE
 * `signInWith*` so the just-acquired refresh token lands in the right
 * storage tier from the start.
 */
export async function setRememberMe(remember: boolean): Promise<void> {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(REMEMBER_ME_KEY, remember ? "true" : "false");
  }
  await setPersistence(auth, persistenceFor(remember));
}

/**
 * Resolves once Firebase has restored the persisted auth state. Safe to await
 * at any time — Firebase memoizes the readiness internally. A storage tier
 * that is unavailable (e.g. Safari private mode) is skipped by the SDK, which
 * falls back to in-memory.
 */
export const authReady: Promise<void> = auth.authStateReady();

/**
 * Wait for Firebase to flush a newly signed-in user into `auth.currentUser`
 * (the moment at which `onIdTokenChanged` fires). Use this between a
 * `signInWith*` call and a subsequent `navigate(...)` to avoid the
 * RequireAuth → /login race. Resolves immediately if a user is already set,
 * or on timeout so the worst case is today's behavior.
 */
export function waitForAuthUser(timeoutMs = 2000): Promise<User | null> {
  return new Promise((resolve) => {
    if (auth.currentUser) return resolve(auth.currentUser);
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(auth.currentUser);
    }, timeoutMs);
    const unsubscribe = onIdTokenChanged(auth, (u) => {
      if (u) {
        clearTimeout(timer);
        unsubscribe();
        resolve(u);
      }
    });
  });
}

export { auth };

/**
 * True when the signed-in user authenticated with email/password AND has not
 * yet verified their email. Google logins are always verified, so they never
 * match. Used to gate unverified password accounts out of the app until they
 * confirm ownership of the address (mirrors the backend `EMAIL_NOT_VERIFIED`
 * gate in `isAuth`).
 */
export function isPasswordUserUnverified(user: User | null): boolean {
  if (!user) return false;
  const isPassword = user.providerData.some((p) => p.providerId === "password");
  return isPassword && !user.emailVerified;
}

/** Narrow an unknown thrown value to a Firebase Auth error code, if present. */
export function getFirebaseCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "code" in err) {
    const code = (err as { code: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}
